import { createAsyncThunk } from "@reduxjs/toolkit";

import { instance } from "../../axios/axios";

// The BFF sends the session JWT in the response's `Authorization: Bearer <jwt>`
// header. Returns the bare token, or null when the header is absent.
export const tokenFromAuthHeader = (headers) => {
  const raw = headers?.authorization ?? headers?.Authorization;
  if (typeof raw !== "string") return null;
  const token = raw.replace(/^Bearer\s+/i, "").trim();
  return token || null;
};

// Friend texted-code sign-in (msg 1a1a9f1d57db2a7e): login({ phone, code })
// posts to verify-code, which answers exactly like /login (Bearer header +
// record), so the same fulfilled/rejected reducers handle both.
export const FRIEND_PHONE_CONFIG_URL = "user/portal/auth/friend-phone/config";
export const FRIEND_PHONE_REQUEST_URL = "user/portal/auth/friend-phone/request-code";
export const FRIEND_PHONE_VERIFY_URL = "user/portal/auth/friend-phone/verify-code";

export const login = createAsyncThunk(
  "auth/login",
  async ({ username, password, phone, code }, { rejectWithValue }) => {
    try {
      const config = {
        headers: {
          "Content-Type": "application/json",
        },
      };
      const friendCode = code !== undefined;
      const response = await instance.post(
        friendCode ? FRIEND_PHONE_VERIFY_URL : "user/portal/login",
        friendCode ? { phone, code } : { username, password },
        config
      );
      const data = response.data;
      // Header path (preferred): the token authSlice reads (`payload.data.token`)
      // is taken from the Authorization header. Fallback: when the header is
      // absent, the body token already in data.data.token is kept, so a missing
      // header never logs anyone out.
      const headerToken = tokenFromAuthHeader(response.headers);
      if (headerToken && data && data.data && typeof data.data === "object") {
        data.data.token = headerToken;
      }
      return data;
    } catch (error) {
      // return custom error message from backend if present
      if (error.response && error.response.data.message) {
        return rejectWithValue(error.response.data.message);
      } else {
        return rejectWithValue(error.message);
      }
    }
  }
);

// Exchange a single-use SMS / welcome-email magic-link token for a portal
// session JWT. Public endpoint — the token IS the credential. See
// tech_map/systems/portal/sms-magic-link-spec.md.
export const redeemMagicLink = createAsyncThunk(
  "auth/redeemMagicLink",
  async ({ token }, { rejectWithValue }) => {
    try {
      const config = {
        headers: {
          "Content-Type": "application/json",
        },
      };
      const { data } = await instance.post(
        "user/portal/auth/redeem-magic-link",
        { token },
        config
      );
      return data;
    } catch (error) {
      if (error.response && error.response.data && error.response.data.message) {
        return rejectWithValue(error.response.data.message);
      }
      return rejectWithValue(error.message);
    }
  }
);

export const forgetPassword = createAsyncThunk(
  "auth/forgetPassword",
  async ({ email }, { rejectWithValue }) => {
    try {
      const config = {
        headers: {
          "Content-Type": "application/json",
        },
      };
      const { data } = await instance.post(
        "user/portal/forget-password",
        { email },
        config
      );
      return data;
    } catch (error) {
      // return custom error message from backend if present
      if (error.response && error.response.data.message) {
        return rejectWithValue(error.response.data.message);
      } else {
        return rejectWithValue(error.message);
      }
    }
  }
);

// Is Friend phone sign-in switched on? Any failure reads as off (the link
// simply does not show).
export const fetchFriendPhoneLoginEnabled = async () => {
  try {
    const { data } = await instance.get(FRIEND_PHONE_CONFIG_URL);
    return data?.data?.enabled === true;
  } catch {
    return false;
  }
};

// Ask the BFF to text a code. It answers the same message whether or not the
// phone belongs to a Friend; that message is returned for display.
export const requestFriendPhoneCode = async (phone) => {
  try {
    const { data } = await instance.post(FRIEND_PHONE_REQUEST_URL, { phone });
    return { ok: true, message: data?.message || "" };
  } catch (error) {
    return { ok: false, message: error.response?.data?.message || error.message };
  }
};
